/**
 * ProductSubscriptions Class
 * Handles the logic for product subscriptions including variant/selling plan selection,
 * UI updates, and synchronization with multiple forms.
 */
class ProductSubscriptions extends HTMLElement {
    constructor() {
        super();

        /** @type {Object} Selectors used for DOM element queries */
        this.selectors = {
            sellingPlanHiddenInput: 'input[name="selling_plan"]',
            nativeSelect: '[data-native-select]',
            customSelect: '[data-custom-select]',
            customToggle: '[data-custom-select-toggle]',
            customList: '[data-custom-select-list]',
            customItems: '[data-custom-select-item]',
            radioInputs: 'input[name="purchase_option"]',
            wrappers: '.product-subscriptions__option',
            selectedValue: '.selling-plan-selector__selected-value'
        };

        /** @type {Object} Cached DOM elements */
        this.dom = {};

        /** @type {Function} Bound event handlers for document listeners */
        this.boundHandleDocumentClick = this.handleDocumentClick.bind(this);
        this.boundHandleDocumentKeyDown = this.handleDocumentKeyDown.bind(this);
    }

    connectedCallback() {
        this.init();
    }

    disconnectedCallback() {
        document.removeEventListener('click', this.boundHandleDocumentClick);
        document.removeEventListener('keydown', this.boundHandleDocumentKeyDown);
    }

    /**
     * Initializes the subscription logic.
     */
    init() {
        this.cacheDOMElements();
        if (!this.dom.sellingPlanHiddenInput) return;

        this.bindEvents();
        this.initializeState();
    }

    /**
     * Caches DOM elements scoped to this component.
     */
    cacheDOMElements() {
        this.dom = {
            sellingPlanHiddenInput: this.querySelector(this.selectors.sellingPlanHiddenInput),
            nativeSelect: this.querySelector(this.selectors.nativeSelect),
            customSelect: this.querySelector(this.selectors.customSelect),
            customToggle: this.querySelector(this.selectors.customToggle),
            customList: this.querySelector(this.selectors.customList),
            customItems: this.querySelectorAll(this.selectors.customItems),
            radioInputs: this.querySelectorAll(this.selectors.radioInputs),
            wrappers: this.querySelectorAll(this.selectors.wrappers)
        };

        this.formId = this.dom.sellingPlanHiddenInput?.getAttribute('form') || this.closest('form')?.id || this.querySelector('form')?.id;
    }

    /**
     * Binds event listeners for purchase options and selling plan selection.
     */
    bindEvents() {
        if (this.dom.customToggle && this.dom.customList) {
            this.dom.customToggle.addEventListener('click', (e) => this.handleCustomToggleClick(e));
            this.dom.customToggle.addEventListener('keydown', (e) => this.handleToggleKeyDown(e));
            document.addEventListener('click', this.boundHandleDocumentClick);
            document.addEventListener('keydown', this.boundHandleDocumentKeyDown);
        }

        this.dom.customItems.forEach((item, index) => {
            item.addEventListener('click', (e) => this.handleCustomItemClick(e, item));
            item.addEventListener('keydown', (e) => this.handleItemKeyDown(e, item, index));
        });

        this.dom.radioInputs.forEach(radio => {
            radio.addEventListener('change', () => this.updateActiveUI());
        });

        if (this.dom.nativeSelect) {
            this.dom.nativeSelect.addEventListener('change', () => this.handleNativeSelectChange());
        }

        this.dom.wrappers.forEach(wrapper => {
            wrapper.addEventListener('click', (e) => this.handleWrapperClick(e, wrapper));
        });
    }

    /**
     * Sets the initial UI state.
     */
    initializeState() {
        this.updateActiveUI();
    }

    /**
     * Handles keydown events on the document.
     * @param {KeyboardEvent} e 
     */
    handleDocumentKeyDown(e) {
        if (e.key === 'Escape' && this.dom.customSelect.classList.contains('is-open')) {
            this.closeCustomSelect();
        }
    }

    /**
     * Handles keydown events on the toggle button.
     * @param {KeyboardEvent} e 
     */
    handleToggleKeyDown(e) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!this.dom.customSelect.classList.contains('is-open')) {
                this.openCustomSelect();
            }
            if (this.dom.customItems.length > 0) {
                const selectedItem = Array.from(this.dom.customItems).find(i => i.classList.contains('selected')) || this.dom.customItems[0];
                selectedItem.focus();
            }
        }
    }

    /**
     * Handles keydown events on list items.
     * @param {KeyboardEvent} e 
     * @param {HTMLElement} item 
     * @param {number} index 
     */
    handleItemKeyDown(e, item, index) {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const nextItem = this.dom.customItems[index + 1] || this.dom.customItems[0];
            nextItem.focus();
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const prevItem = this.dom.customItems[index - 1] || this.dom.customItems[this.dom.customItems.length - 1];
            prevItem.focus();
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.handleCustomItemClick(e, item);
        } else if (e.key === 'Escape') {
            this.closeCustomSelect();
        } else if (e.key === 'Tab') {
            this.closeCustomSelect();
        }
    }

    /**
     * Handles the click event on the custom select toggle.
     * @param {Event} e 
     */
    handleCustomToggleClick(e) {
        e.stopPropagation();
        const isOpen = this.dom.customSelect.classList.contains('is-open');
        if (isOpen) {
            this.closeCustomSelect();
        } else {
            this.openCustomSelect();
        }
    }

    /**
     * Opens the custom select.
     */
    openCustomSelect() {
        this.dom.customSelect.classList.add('is-open');
        this.dom.customList.style.display = 'block';
        this.dom.customToggle.setAttribute('aria-expanded', 'true');
        this.selectSubscriptionOption();
    }

    /**
     * Closes the custom select.
     */
    closeCustomSelect() {
        this.dom.customSelect.classList.remove('is-open');
        this.dom.customList.style.display = 'none';
        this.dom.customToggle.setAttribute('aria-expanded', 'false');
        this.dom.customToggle.focus();
    }

    /**
     * Ensures the subscription radio option is selected.
     */
    selectSubscriptionOption() {
        const subRadio = Array.from(this.dom.radioInputs).find(radio => radio.value === 'subscription');
        if (subRadio && !subRadio.checked) {
            subRadio.checked = true;
            subRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    /**
     * Handles clicks outside the custom select to close it.
     * @param {Event} e 
     */
    handleDocumentClick(e) {
        if (this.dom.customSelect && !this.dom.customSelect.contains(e.target)) {
            if (this.dom.customSelect.classList.contains('is-open')) {
                this.closeCustomSelect();
            }
        }
    }

    /**
     * Handles the click event on a custom select item.
     * @param {Event} e 
     * @param {HTMLElement} item 
     */
    handleCustomItemClick(e, item) {
        e.stopPropagation();
        const value = item.dataset.value;
        const text = item.textContent.trim();

        this.dom.customItems.forEach(i => {
            i.classList.remove('selected');
            i.setAttribute('aria-selected', 'false');
        });
        item.classList.add('selected');
        item.setAttribute('aria-selected', 'true');

        if (this.dom.customToggle) {
            const selectedTextElement = this.dom.customToggle.querySelector(this.selectors.selectedValue);
            if (selectedTextElement) selectedTextElement.textContent = text;
        }

        this.closeCustomSelect();

        this.selectSubscriptionOption();

        if (this.dom.nativeSelect) {
            this.dom.nativeSelect.value = value;
            this.dom.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    /**
     * Handles changes in the native select element.
     */
    handleNativeSelectChange() {
        const subRadio = this.querySelector('input[value="subscription"]');
        if (subRadio && subRadio.checked) {
            this.syncSellingPlan(this.dom.nativeSelect.value);
        }
    }

    /**
     * Handles clicks on the option wrappers.
     * @param {Event} e 
     * @param {HTMLElement} wrapper 
     */
    handleWrapperClick(e, wrapper) {
        if (!e.target.closest(this.selectors.customSelect) && !e.target.closest('input')) {
            const radio = wrapper.querySelector(this.selectors.radioInputs);
            if (radio && !radio.checked) {
                radio.checked = true;
                radio.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
    }

    /**
     * Updates the UI based on the selected purchase option.
     */
    updateActiveUI() {
        const selectedRadio = this.querySelector('input[name="purchase_option"]:checked');
        if (!selectedRadio) return;

        const type = selectedRadio.value;

        this.dom.wrappers.forEach(wrapper => {
            const isSelected = wrapper.dataset.type === type;
            wrapper.classList.toggle('selected', isSelected);

            const radio = wrapper.querySelector(this.selectors.radioInputs);
            if (radio) {
                radio.setAttribute('aria-expanded', isSelected && type === 'subscription' ? 'true' : 'false');
            }
        });

        if (type === 'subscription') {
            const planId = this.dom.nativeSelect ? this.dom.nativeSelect.value : '';
            this.syncSellingPlan(planId);
        } else {
            this.syncSellingPlan('');
        }
    }

    /**
     * Synchronizes the selling plan value across linked inputs and forms.
     * @param {string} value 
     */
    syncSellingPlan(value) {
        if (this.dom.sellingPlanHiddenInput) {
            this.dom.sellingPlanHiddenInput.value = value;
            this.dom.sellingPlanHiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (this.formId) {
            const formSelector = `#${this.formId}, form[id="${this.formId}"]`;
            const forms = document.querySelectorAll(formSelector);

            forms.forEach(form => {
                const externalInputs = form.querySelectorAll('input[name="selling_plan"]');
                externalInputs.forEach(input => {
                    if (input !== this.dom.sellingPlanHiddenInput) {
                        input.value = value;
                        input.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                });
            });

            const globalInputs = document.querySelectorAll(`input[name="selling_plan"][form="${this.formId}"]`);
            globalInputs.forEach(input => {
                if (input !== this.dom.sellingPlanHiddenInput) {
                    input.value = value;
                    input.dispatchEvent(new Event('change', { bubbles: true }));
                }
            });
        }
    }
}

if (!customElements.get('product-subscriptions')) {
    customElements.define('product-subscriptions', ProductSubscriptions);
}

